import * as _ from 'lodash';

import type { AccessLevelOption } from '@/utils/accessUtils';
import { accessUtils, DefaultAccessLevelOptions } from '@/utils/accessUtils';
import navigationUtils from '@/utils/navigationUtils';

import type { ISupportedVariantList } from '../../../constants/supportedVariants';
import type { Category, ListItem, StorageAdapter, Tag, TranscodeVariant } from '../../../models';
import type { AccessibleOU } from '../../../models/FuelUserAccess';
import type {
  ChannelRefreshControl,
  ContentSharingBehaviors,
  ExternalServiceConnector,
  OU,
  OUFeatures,
  OuMetaData,
  StreamingEngineConnectionInfo,
} from '../../../models/Ou';
import { ValidationErrors } from '../../../models/ValidationErrors';
import clientUtils from '../../../utils/clientUtils';
import itemUtils from '../../../utils/itemUtils';

export class OuForm {
  mode: 'new' | 'existing' = 'new';
  permission: string = '';
  ouId: string = '';
  parentId: string = '';
  parentName: string | null = null;
  schemaId: string = '';
  ouName: string = '';
  ouCode: string = '';
  ouUrl: string = '';
  isEnabled: boolean = true;
  isDeleted: boolean = false;
  createdBy: string = '';
  createdDate: Date = new Date();
  modifiedBy: string = '';
  modifiedDate: Date = new Date();
  timezone: string = 'America/Los_Angeles';
  dashboardUrl: string = '';
  analyticsElasticSearchUrl: string = '';
  ouSlug: string = '';
  ouType: string = '';
  ouPath: string = '';
  defaultVariantCodes: string[] = [];
  categories: Category[] = [];
  storageAdapters: StorageAdapter[] = [];
  tags: Tag[] = [];
  id: string = '';
  metaData: OuMetaData;
  entityType: string = 'Ou';
  ouData: OU = null;
  irisTVOUClientToken: string = '';
  speechToTextOnIngest: boolean = false;
  createCCOnIngest: boolean = false;
  showCreateCCButton: boolean = false;
  playerEmbedCodeTemplate: string = '';
  expiredAssetAutoRemove: number = 0;
  defaultTargetDuration?: number;
  targetDurations: TranscodeVariant[];
  selectedDefaultTargetDuration?: TranscodeVariant;
  frameRates: ListItem[] = [];
  selectedFrameRate?: ListItem | null = null;
  selectedIngestVariant: TranscodeVariant;
  allVariants: TranscodeVariant[] = [];
  supportedVariants: ISupportedVariantList[] = [];
  selectedVariants: TranscodeVariant[] = [];
  tempVariants: TranscodeVariant[] = null; // temporarily stores a list until the supported variants are saved
  variantCodes: TranscodeVariant[]; // stores a list for the default variants select options
  ingestVariant?: string;
  selectedIngestVariants?: TranscodeVariant[];
  selectedDefaultVariants?: TranscodeVariant[];
  errors: ValidationErrors = new ValidationErrors([]);
  automatedCleanupEnabled: boolean = false;
  softDeleteAssetsAfterDuration: string = null;
  hardDeleteAssetsAfterDuration: string = null;
  softDeleteIgnoreTags: string[] = [];
  softDeleteIgnoreCategories: Category[] = [];
  oldOuName: string = '';
  showRobustSubClippingOption: boolean = false;
  seConnectionInfo: StreamingEngineConnectionInfo | null = null;
  enableSeConnectionInfo: boolean = false;
  isTrickPlayEnabled: boolean = false;
  defaultChannelRefreshControls: ChannelRefreshControl | null;
  enableSite: boolean = false;
  siteId: string = null;
  enableLiveTranscription: boolean = false;
  allowMultipleCategories: boolean = true;
  ouFeatures: OUFeatures = null;
  enablePaywall: boolean = false;
  enableCrossOuSharing: boolean = false;
  contentSharingBehavior: ContentSharingBehaviors = 'DoNotShare';
  defaultAccessLevelOptions: AccessLevelOption[] = DefaultAccessLevelOptions; // For the ou admin dropdown to select default access level
  defaultAccessLevel: AccessLevelOption = DefaultAccessLevelOptions[0];

  // adConfig
  allowExpertMode: boolean = false;
  serverSideAdPlatformId: string | null = 'SpringServe';
  ssaiSupplyTagId: string | null = null;
  clientSideAdPlatformId: string | null = 'SpringServe';
  csaiSupplyTagId: string | null = null;
  demandPlatformId: string | null = 'SpringServe';
  demandPartnerId: string | null = null;
  maxLiveClipMinutes: number | null = 240;

  originalData: OU = null;

  static create(parentId: string): OuForm {
    const form = new OuForm();
    form.mode = 'new';
    form.parentId = parentId;
    form.seConnectionInfo = {
      baseApiUrl: '',
      baseContentPath: '',
    };
    form.metaData = { seConnectionInfo: null };
    return form;
  }

  static createFromData(
    data: OU,
    allVariants: TranscodeVariant[],
    variantCodes: TranscodeVariant[],
    accessibleOUs: AccessibleOU[] = [],
    flags
  ): OuForm {
    const form = new OuForm();
    form.originalData = _.cloneDeep(data);
    const parentOu = accessibleOUs.find((ou) => ou.ouId === data.parentId);
    form.parentName = parentOu && parentOu.ouName ? parentOu.ouName : null;
    form.allVariants = form.generateList(allVariants);
    if (data && data.metaData && data.metaData.allowedVariants && data.metaData.allowedVariants.length > 0) {
      const { allowedVariants } = data.metaData;
      form.selectedVariants = form.allVariants.filter((v) => allowedVariants.indexOf(v.variantCode) >= 0);
    }
    form.variantCodes = form.generateList(variantCodes);
    form.supportedVariants = itemUtils.getSupportedVariants(allVariants);

    form.loadOuData(data, flags);

    form.getDefaultAccessLevelOptions(data);
    form.getTargetDurations(data);
    form.getFrameRates(data);
    form.getSelectedIngestVariant(data);
    form.getSelectedIngestVariants(data);
    form.getSelectedDefaultVariants(data);

    return form;
  }

  loadOuData(data: OU, flags) {
    // console.log('loadOuData', { data });
    const getTokenValue = (externalServiceConnectors: ExternalServiceConnector[], serviceName) => {
      const externalServiceConnector = externalServiceConnectors.find((item) => item.serviceName === serviceName);
      if (externalServiceConnector) return externalServiceConnector.connectionParams.irisTVOUClientToken;
      return null;
    };
    this.mode = 'existing';
    this.ouId = data.ouId;
    this.ouName = data.ouName;
    this.oldOuName = data.ouName;
    this.parentId = data.parentId;
    this.ouCode = data?.metaData?.ouCodeName;
    this.ouUrl = data?.metaData?.ouUrl;
    this.schemaId = data.schemaId;
    this.isEnabled = data.isEnabled;
    this.isDeleted = data.isDeleted;
    this.createdBy = data.createdBy;
    this.createdDate = data.createdDate;
    this.modifiedBy = data.modifiedBy;
    this.modifiedDate = data.modifiedDate;
    this.timezone = data.timezone;
    this.dashboardUrl = data.dashboardUrl;
    this.analyticsElasticSearchUrl = data.analyticsElasticSearchUrl;
    this.defaultVariantCodes = data.defaultVariantCodes;
    this.ouPath = data.ouPath;
    this.ouSlug = data.ouSlug;
    this.ouType = data.ouType;
    this.categories = data.categories;
    this.tags = data.tags;
    this.storageAdapters = data.storageAdapters;
    this.metaData = data.metaData;
    if (this.metaData) {
      this.speechToTextOnIngest = this.metaData.speechToTextOnIngest;
      this.createCCOnIngest = this.metaData.createCCOnIngest;
      this.showCreateCCButton = this.metaData.showCreateCCButton;
      this.playerEmbedCodeTemplate = this.metaData.playerEmbedCodeTemplate;
      this.expiredAssetAutoRemove = this.metaData.expiredAssetAutoRemove;
      this.showRobustSubClippingOption = this.metaData.showRobustSubClippingOption;
      this.isTrickPlayEnabled = data.metaData.isTrickPlayEnabled;
      this.enablePaywall = data.metaData.enablePaywall;
      this.enableCrossOuSharing = data.metaData.enableCrossOuSharing === true;
      this.contentSharingBehavior = data.metaData.contentSharing.behavior;
      this.enableLiveTranscription = data.metaData.enableLiveTranscription;
      this.maxLiveClipMinutes = data.metaData.maxLiveClipMinutes ? data.metaData.maxLiveClipMinutes : 240;
      this.allowMultipleCategories = data.metaData.allowMultipleCategories !== false;
      if (data?.metaData?.ouFeatures != null) {
        this.ouFeatures = _.cloneDeep(data.metaData.ouFeatures);
        if (this.ouFeatures.navigation == null) {
          this.ouFeatures.navigation = {};
        }

        const defaultFeatures = navigationUtils.getDefaultFeatures();
        const mergedObject = _.mergeWith({}, this.ouFeatures.navigation, defaultFeatures, (objValue, srcValue) => {
          // If objValue is not undefined, it means the key already exists in the destination object.
          // In this case, we return objValue to keep the existing value.
          if (objValue !== undefined) {
            return objValue;
          }
          // If objValue is undefined, it means the key does not exist in the destination object,
          // so we take the value from the source object.
          return srcValue;
        });

        this.ouFeatures.navigation = mergedObject;

        if (navigationUtils.compareToCustomerType(this.ouFeatures.navigation, 'channels')) {
          this.ouFeatures.customerType = 'channels';
        } else if (navigationUtils.compareToCustomerType(this.ouFeatures.navigation, 'apps')) {
          this.ouFeatures.customerType = 'apps';
        } else {
          this.ouFeatures.customerType = 'custom';
        }
      } else {
        // console.log('setting default ouFeatures');
        const defaultChannelFeatures = navigationUtils.getDefaultChannelFeatures();
        this.ouFeatures = { customerType: 'channels', navigation: defaultChannelFeatures };
      }
      if (this.metaData.externalServiceConnectors && this.metaData.externalServiceConnectors.length) {
        this.irisTVOUClientToken = getTokenValue(this.metaData.externalServiceConnectors, 'IrisTV');
      }
      if (this.metaData.automatedCleanupSettings) {
        this.automatedCleanupEnabled = this.metaData.automatedCleanupSettings.automatedCleanupEnabled;
        this.softDeleteAssetsAfterDuration = this.metaData.automatedCleanupSettings.softDeleteAssetsAfterDuration;
        this.hardDeleteAssetsAfterDuration = this.metaData.automatedCleanupSettings.hardDeleteAssetsAfterDuration;
        this.softDeleteIgnoreTags = this.metaData.automatedCleanupSettings.softDeleteIgnoreTags ?? [];
        this.softDeleteIgnoreCategories = this.metaData.automatedCleanupSettings.softDeleteIgnoreCategories ?? [];
      }
    }
    if (this.metaData.seConnectionInfo) {
      this.enableSeConnectionInfo = true;
      this.seConnectionInfo = {
        baseApiUrl: this.metaData.seConnectionInfo.baseApiUrl,
        baseContentPath: this.metaData.seConnectionInfo.baseContentPath,
      };
    }
    if (this.metaData.defaultChannelRefreshControls) {
      this.defaultChannelRefreshControls = {
        minTimeBetweenRefresh: this.metaData.defaultChannelRefreshControls.minTimeBetweenRefresh
          ? clientUtils.secondsToTimeSpanFormat(this.metaData.defaultChannelRefreshControls.minTimeBetweenRefresh).seconds
          : 0,
        maxTimeBetweenRefresh: this.metaData.defaultChannelRefreshControls.maxTimeBetweenRefresh
          ? clientUtils.secondsToTimeSpanFormat(this.metaData.defaultChannelRefreshControls.maxTimeBetweenRefresh).seconds
          : 0,
      };
    }
    this.id = data.id;
    this.ouData = data;
    this.entityType = data.entityType;

    // adConfiguration
    if (data.metaData != null && data.metaData.adConfiguration != null) {
      this.allowExpertMode = data.metaData.adConfiguration.allowExpertMode;
      this.serverSideAdPlatformId = data.metaData.adConfiguration.serverSideAdPlatformId;
      this.ssaiSupplyTagId = data.metaData.adConfiguration.ssaiSupplyTagId;
      this.clientSideAdPlatformId = data.metaData.adConfiguration.clientSideAdPlatformId;
      this.csaiSupplyTagId = data.metaData.adConfiguration.csaiSupplyTagId;
      this.demandPlatformId = data.metaData.adConfiguration.demandPlatformId;
      this.demandPartnerId = data.metaData.adConfiguration.demandPartnerId;
    }
    this.enableSite = data.metaData.enableSite;
    this.siteId = data.metaData.siteId;

    // console.log('loadOUData finished', { ouData: _.cloneDeep(this) });
  }

  getFrameRates(ouData: OU) {
    // console.log('getFrameRates', this.variantCodes);
    if (this.variantCodes && this.variantCodes.length > 0) {
      const copy = _.cloneDeep(this.variantCodes);
      const uniqFrameRateOptions = _.uniq(this.variantCodes.map((v) => v.frameRate)).map((f) => {
        return { id: f, name: f };
      });
      this.frameRates = uniqFrameRateOptions;

      const { defaultVariants } = ouData.metaData;
      const firstDefaultVariant = this.variantCodes.find(function (item) {
        return defaultVariants.indexOf(item.variantCode) >= 0;
      });
      if (firstDefaultVariant) {
        const found = this.frameRates.find((f) => f.id == firstDefaultVariant?.frameRate);
        if (found) {
          this.selectedFrameRate = found;
        }
      }
    } else {
      this.frameRates = [];
    }
  }

  getDefaultAccessLevelOptions(data: OU) {
    this.defaultAccessLevelOptions = accessUtils.getAccessLevelOptions(data);
    let defaultIndex = 0;
    if (data?.metaData?.defaultAccessLevel) {
      const foundIndex = this.defaultAccessLevelOptions.findIndex((x) => x.value === data.metaData.defaultAccessLevel);
      if (foundIndex > -1) {
        defaultIndex = foundIndex;
      }
    }
    this.defaultAccessLevel = this.defaultAccessLevelOptions[defaultIndex];
  }

  getTargetDurations(data: OU) {
    if (this.variantCodes && this.variantCodes.length > 0) {
      const variantCodesSorted = _.sortBy(this.variantCodes, [
        function (o) {
          return o.segmentLength;
        },
      ]);
      const distinctTargetDurations = [];
      let lastTargetDuration = 0;
      for (let i = 0; i < variantCodesSorted.length; i++) {
        const element = variantCodesSorted[i];
        if (element.segmentLength !== lastTargetDuration) {
          lastTargetDuration = element.segmentLength;
          distinctTargetDurations.push(element);
          const metaData = data ? data.metaData : null;
          if (metaData && metaData.defaultTargetDuration === element.segmentLength) {
            this.selectedDefaultTargetDuration = element;
          }
        }
      }
      this.targetDurations = distinctTargetDurations;
    }
  }
  static getAvailableVariantCodes(
    variantCodes: TranscodeVariant[],
    td: TranscodeVariant,
    frameRateOption: ListItem | null = null
  ) {
    if (variantCodes == null || variantCodes.length <= 0 || td == null) {
      return [];
    }
    let availableVariantCodes = variantCodes.filter((v) => v.segmentLength === td.segmentLength);
    if (frameRateOption != null) {
      availableVariantCodes = availableVariantCodes.filter((v) => v.frameRate === frameRateOption.id);
    }
    return availableVariantCodes;
  }
  getSelectedIngestVariant(ouData: OU) {
    const existingIngestVariant = ouData.metaData.ingestVariant;
    if (existingIngestVariant) {
      this.selectedIngestVariant = _.find(this.variantCodes, function (v) {
        return v.variantCode === existingIngestVariant;
      });
    }
  }

  getSelectedIngestVariants(ouData: OU) {
    const ingestVariants = ouData.metaData?.ingestVariants || [];
    this.selectedIngestVariants = this.variantCodes.filter(function (item) {
      return ingestVariants.indexOf(item.variantCode) >= 0;
    });
  }

  getSelectedDefaultVariants(ouData: OU) {
    const { defaultVariants } = ouData.metaData;
    this.selectedDefaultVariants = this.variantCodes.filter(function (item) {
      return defaultVariants.indexOf(item.variantCode) >= 0;
    });
  }
  generateList(list) {
    const variantList = list;
    variantList.forEach((e) => {
      if (e) {
        if (e && !e.id) {
          e.id = e.variantCode;
          e.tagText = e.variantCode.replace('AWS', '');
          e.group = `${e.segmentLength}S ${e.frameRate}FPS`;
          e.name = `${e.variantCode} (${e.width}x${e.height} ${e.segmentLength}S ${e.frameRate}FPS)`;
        }
      }
    });
    return variantList;
  }
}
